import React, { useEffect, useState } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import styled from 'styled-components';

import { isCommunityCCRC } from 'components/Community/utils';
import Button from 'components/lib/Button';
import GradientDivider from 'components/lib/GradientDivider';
import InputNumber from 'components/lib/InputNumber';
import Popover from 'components/lib/Popover';
import Select from 'components/lib/Select';
import { useAppSelector } from 'store/hook';
import { ApartmentType } from 'types/accommodations';
import { isOverallApartmentType } from 'utils/data/apartmentTypes';
import { normalizeValue } from 'utils/helpers/form';
import { flattenObjects } from 'utils/helpers/object';

import CloseIcon from '../../../common/Icons/CloseIcon';
import { APARTMENT_TYPE } from '../../../constants';
import ShopDataModalAddButtonPill from '../common/ShopDataModalAddButtonPill';
import ShopDataModalContentHeader from '../common/ShopDataModalContentHeader';
import { FIELD_CATEGORY, OCCUPANCIES_MODE, VACANCY_TYPE } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';
import { getTestIdInitials } from '../testIdUtils';

const PercentageLabel = styled.div`
  color: var(--text-primary);
  font-size: 20px;

  span {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 5px;
  }
`;

const StyledCloseIcon = styled.div`
  display: flex;
  visibility: hidden;
  margin-right: 5px;
  margin-left: 5px;
`;

const Labels = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 31px;
  position: sticky;
  top: 0px;
  z-index: 100;
  background-color: var(--white);
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 31px;
`;

const RowLabel = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  text-align: right;
  color: var(--text-primary);
  min-width: 165px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  &:hover {
    ${StyledCloseIcon} {
      cursor: pointer;
      visibility: visible;
    }
  }
`;

const ColumnLabel = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  width: 120px;
  padding-top: 15px;
  padding-bottom: 5px;
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: 15px;
  gap: 4px;
`;

const CategoryButton = styled(Button)`
  border-radius: 25px;
  width: 84px;
  height: 24px;
  font-size: 12px;
  padding: 0;
`;

const OCCUPANCIES_VACANCY_TYPE_FIELD = 'vacancy_type';

const ShopDataModalOccupancies = () => {
  const apartmentTypes = useAppSelector((state) => state.global.apartmentTypes);
  const livingTypes = useAppSelector((state) => state.global.livingTypes);

  const { form, community, currentData, disabled, setCurrentData, handleDataStatusChanged } = useShopDataModalContext();
  const [occupancyMode, setOccupancyMode] = useState<string>(OCCUPANCIES_MODE.CARE_TYPE);
  const [fieldCategory, setFieldCategory] = useState<string>(FIELD_CATEGORY.OCCUPANCIES_OVERALL);

  const roomTypeOptions = (apartmentTypes ?? [])
    .filter((item: ApartmentType) => !isOverallApartmentType(item))
    .map((item: ApartmentType) => ({
      value: item.id,
      label: item.type,
    }));

  useEffect(() => {
    setFieldCategory(
      occupancyMode === OCCUPANCIES_MODE.CARE_TYPE ? FIELD_CATEGORY.OCCUPANCIES_OVERALL : FIELD_CATEGORY.OCCUPANCIES
    );
  }, [occupancyMode]);

  const handleValuesChange = (values: any, allValues: any) => {
    const newOccupanciesData = allValues[fieldCategory];
    const changedOccupanciesLivingType = values[fieldCategory];
    const changedOccupanciesLivingTypeKey = Object.keys(changedOccupanciesLivingType)[0];
    const changedOccupanciesField = Object.keys(changedOccupanciesLivingType[changedOccupanciesLivingTypeKey])[0];

    handleDataStatusChanged([fieldCategory]);

    // If changed value is Vacancy Type we want to reset all the fields related to that Living Type entry
    if (changedOccupanciesField === OCCUPANCIES_VACANCY_TYPE_FIELD) {
      const changedOccupanciesFieldValue = Object.values(
        changedOccupanciesLivingType[changedOccupanciesLivingTypeKey]
      )[0];

      newOccupanciesData[changedOccupanciesLivingTypeKey] = {
        ...newOccupanciesData[changedOccupanciesLivingTypeKey],
        vacancy_type: changedOccupanciesFieldValue,
        free_apartments_units: null,
        total_apartments_units: null,
        occupied_apartments_units: null,
        pct_occupancy: null,
        calculated_pct_occupancy: null,
      };
    }

    setCurrentData((prevState: any) => ({
      ...prevState,
      [fieldCategory]: newOccupanciesData,
    }));
  };

  useEffect(() => {
    form?.setFieldsValue({
      [fieldCategory]: currentData[fieldCategory],
    });
  }, [currentData[fieldCategory], form, occupancyMode]);

  const showOccupanciesInputTypeSelector = !isCommunityCCRC(community?.rental_types || []);

  return (
    <>
      <Form form={form} disabled={disabled} onValuesChange={handleValuesChange}>
        <ShopDataModalContentHeader title="Occupancies">
          {showOccupanciesInputTypeSelector && (
            <CategoryContainer>
              <CategoryButton
                onClick={() => setOccupancyMode(OCCUPANCIES_MODE.CARE_TYPE)}
                type={occupancyMode === OCCUPANCIES_MODE.CARE_TYPE ? 'primary' : undefined}
                data-test-id="sd-modal-general-occupancies-room-type-btn"
              >
                Care Type
              </CategoryButton>
              <CategoryButton
                onClick={() => setOccupancyMode(OCCUPANCIES_MODE.ROOM_TYPE)}
                type={occupancyMode === OCCUPANCIES_MODE.ROOM_TYPE ? 'primary' : undefined}
                data-test-id="sd-modal-general-occupancies-care-type-btn"
              >
                Room Type
              </CategoryButton>
            </CategoryContainer>
          )}
        </ShopDataModalContentHeader>
        <Labels>
          <RowLabel />
          {occupancyMode === OCCUPANCIES_MODE.ROOM_TYPE && <ColumnLabel style={{ width: 185 }}>ROOM TYPE</ColumnLabel>}
          <ColumnLabel>VACANCY TYPE</ColumnLabel>
          <ColumnLabel>OCCUPIED</ColumnLabel>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues[fieldCategory] !== currentValues[fieldCategory]}
          >
            {({ getFieldValue }) => {
              const occupancies = getFieldValue(fieldCategory) || [];
              const flattenedOccupancies = flattenObjects(occupancies);
              const hasUnitVacancyType = flattenedOccupancies.some(
                (item: any) => item?.vacancy_type === VACANCY_TYPE.UNIT
              );

              return <>{hasUnitVacancyType && <ColumnLabel>VACANT</ColumnLabel>}</>;
            }}
          </Form.Item>
        </Labels>
        {livingTypes.map((livingType, index) => {
          const livingTypeKey = livingType.abv as string;
          return (
            <div key={livingType.type}>
              <Row>
                <RowLabel>{livingType.type}</RowLabel>
                <Fields>
                  <Form.List name={[fieldCategory, livingTypeKey]}>
                    {(fields, { add, remove }) => {
                      const livingTypeOccupancies = form?.getFieldValue([fieldCategory, livingTypeKey]) || [];

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {fields.map(({ key, name, ...restField }) => {
                            return (
                              <FieldRow key={key}>
                                <Form.Item noStyle name={[name, 'id']} hidden />
                                <Form.Item noStyle name={[name, 'living_type_id']} hidden />
                                <Form.Item noStyle name={[name, 'total_apartments_units']} hidden />
                                <Form.Item
                                  noStyle
                                  name={[name, 'apartment_type_id']}
                                  hidden={occupancyMode === OCCUPANCIES_MODE.CARE_TYPE}
                                  rules={[
                                    () => ({
                                      validator(_, value) {
                                        const allValues = livingTypeOccupancies.map(
                                          (item: any) => item.apartment_type_id
                                        );

                                        const isDuplicate = allValues.filter((id: number) => id === value).length > 1;

                                        if (isDuplicate) {
                                          return Promise.reject(new Error('This room type has already been selected!'));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Select
                                    className={'rectangular'}
                                    placeholder={'Select Room Type'}
                                    showSearch
                                    optionFilterProp="label"
                                    dropdownStyle={{
                                      padding: 0,
                                      borderRadius: 0,
                                      border: '1px solid var(--line-gray)',
                                      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                                    }}
                                    style={{ height: 36, width: 185 }}
                                    options={roomTypeOptions}
                                  />
                                </Form.Item>
                                <Form.Item noStyle name={[name, OCCUPANCIES_VACANCY_TYPE_FIELD]}>
                                  <Select
                                    className={'rectangular'}
                                    dropdownStyle={{
                                      padding: 0,
                                      borderRadius: 0,
                                      border: '1px solid var(--line-gray)',
                                      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                                    }}
                                    placeholder={'Select Vacancy Type'}
                                    style={{ height: 36, width: 120 }}
                                    options={[
                                      { label: 'Percentage', value: VACANCY_TYPE.PERCENTAGE },
                                      { label: 'Unit', value: VACANCY_TYPE.UNIT },
                                    ]}
                                    data-test-id={`sd-modal-general-occupancies-${getTestIdInitials(livingType.type)}-vacancy-type-dropdown`}
                                  />
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prevValues, currentValues) =>
                                    prevValues[fieldCategory]?.[livingTypeKey]?.[name]?.vacancy_type !==
                                    currentValues[fieldCategory]?.[livingTypeKey]?.[name]?.vacancy_type
                                  }
                                >
                                  {({ getFieldValue }) => {
                                    const vacancyType = getFieldValue([
                                      fieldCategory,
                                      livingTypeKey,
                                      name,
                                      OCCUPANCIES_VACANCY_TYPE_FIELD,
                                    ]);

                                    if (!vacancyType) {
                                      return null;
                                    }

                                    if (vacancyType === VACANCY_TYPE.UNIT) {
                                      return (
                                        <>
                                          <Form.Item
                                            noStyle
                                            name={[name, 'occupied_apartments_units']}
                                            rules={[{ type: 'number', min: 0 }, { required: true }]}
                                            normalize={normalizeValue({ decimals: 1, roundToNearest: 0.5 })}
                                          >
                                            <InputNumber style={{ height: 36, width: 120 }} />
                                          </Form.Item>
                                          <Form.Item
                                            noStyle
                                            name={[name, 'free_apartments_units']}
                                            rules={[{ type: 'number', min: 0 }, { required: true }]}
                                            normalize={normalizeValue({ decimals: 1, roundToNearest: 0.5 })}
                                          >
                                            <InputNumber style={{ height: 36, width: 120 }} />
                                          </Form.Item>
                                        </>
                                      );
                                    }

                                    if (vacancyType === VACANCY_TYPE.PERCENTAGE) {
                                      return (
                                        <Form.Item
                                          noStyle
                                          name={[name, 'pct_occupancy']}
                                          rules={[{ type: 'number', min: 0, max: 100 }, { required: true }]}
                                          normalize={normalizeValue()}
                                        >
                                          <InputNumber style={{ height: 36, width: 120 }} suffix={'%'} />
                                        </Form.Item>
                                      );
                                    }
                                  }}
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  dependencies={[
                                    [name, OCCUPANCIES_VACANCY_TYPE_FIELD],
                                    [name, 'free_apartments_units'],
                                    [name, 'occupied_apartments_units'],
                                  ]}
                                >
                                  {({ getFieldValue }) => {
                                    const vacancyType = getFieldValue([
                                      fieldCategory,
                                      livingTypeKey,
                                      name,
                                      OCCUPANCIES_VACANCY_TYPE_FIELD,
                                    ]);
                                    const vacantValue =
                                      getFieldValue([fieldCategory, livingTypeKey, name, 'free_apartments_units']) || 0;
                                    const occupiedValue =
                                      getFieldValue([
                                        fieldCategory,
                                        livingTypeKey,
                                        name,
                                        'occupied_apartments_units',
                                      ]) || 0;

                                    const total = parseFloat(occupiedValue) + parseFloat(vacantValue);
                                    const occupiedPercentage =
                                      total > 0 ? ((occupiedValue / total) * 100).toFixed(1) : 0;

                                    if (
                                      occupancyMode === OCCUPANCIES_MODE.ROOM_TYPE &&
                                      vacancyType === VACANCY_TYPE.UNIT
                                    ) {
                                      return (
                                        <Popover
                                          placement={'bottom'}
                                          arrow={false}
                                          content={
                                            <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                                              <span style={{ display: 'flex', gap: 4 }}>
                                                <span style={{ fontFamily: 'var(--font-bold)' }}>{total}</span>
                                                <span>total</span>
                                              </span>
                                              <span>·</span>
                                              <span style={{ display: 'flex', gap: 4 }}>
                                                <span style={{ fontFamily: 'var(--font-bold)' }}>
                                                  {occupiedPercentage}%
                                                </span>
                                                <span>occupied</span>
                                              </span>
                                            </div>
                                          }
                                        >
                                          <InfoCircleOutlined />
                                        </Popover>
                                      );
                                    }

                                    if (
                                      vacancyType !== VACANCY_TYPE.UNIT ||
                                      total <= 0 ||
                                      vacantValue < 0 ||
                                      occupiedValue < 0
                                    ) {
                                      return null;
                                    }

                                    return (
                                      <PercentageLabel>
                                        {total}
                                        <span>total</span>
                                        {occupiedPercentage}
                                        <span style={{ color: 'var(--text-primary)', margin: 0 }}>%</span>
                                        <span>occupied</span>
                                      </PercentageLabel>
                                    );
                                  }}
                                </Form.Item>
                                <StyledCloseIcon
                                  onClick={() => {
                                    remove(name);
                                    if (livingTypeOccupancies.length === 1) {
                                      add({
                                        living_type_id: livingType.id,
                                        living_type_abv: livingType.abv,
                                        apartment_type_id:
                                          occupancyMode === OCCUPANCIES_MODE.CARE_TYPE
                                            ? APARTMENT_TYPE.OVERALL
                                            : undefined,
                                      });
                                    }
                                  }}
                                >
                                  <CloseIcon size={20} />
                                </StyledCloseIcon>
                              </FieldRow>
                            );
                          })}
                          {occupancyMode === OCCUPANCIES_MODE.ROOM_TYPE && (
                            <ShopDataModalAddButtonPill
                              label={'Add Room Type'}
                              className="max-width"
                              onClick={() =>
                                add({
                                  living_type_id: livingType.id,
                                  living_type_abv: livingType.abv,
                                })
                              }
                            />
                          )}
                        </div>
                      );
                    }}
                  </Form.List>
                </Fields>
              </Row>
              {index != livingTypes.length - 1 && <GradientDivider />}
            </div>
          );
        })}
      </Form>
    </>
  );
};

export default ShopDataModalOccupancies;
